import { useEffect, useRef } from "react";

/**
 * useTouchCoordinates
 * Normaliza eventos de cursor y gestos multitouch (touchstart, touchmove, touchend, pointermove)
 * al espacio relativo de coordenadas del Canvas.
 */
export function useTouchCoordinates(canvasRef, onPointerUpdate) {
  const isInteracting = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const getCanvasCoords = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    };

    // Mouse / Pointer handlers
    const handlePointerMove = (e) => {
      const { x, y } = getCanvasCoords(e.clientX, e.clientY);
      onPointerUpdate?.({ x, y, isTouch: false, active: true });
    };

    const handlePointerLeave = () => {
      onPointerUpdate?.({ x: -1000, y: -1000, isTouch: false, active: false });
    };

    // Touch handlers (Móviles y Tablets)
    const handleTouchStart = (e) => {
      if (e.touches && e.touches.length > 0) {
        isInteracting.current = true;
        const touch = e.touches[0];
        const { x, y } = getCanvasCoords(touch.clientX, touch.clientY);
        onPointerUpdate?.({ x, y, isTouch: true, active: true });
      }
    };

    const handleTouchMove = (e) => {
      if (!isInteracting.current || !e.touches || e.touches.length === 0) return;
      const touch = e.touches[0];
      const { x, y } = getCanvasCoords(touch.clientX, touch.clientY);
      onPointerUpdate?.({ x, y, isTouch: true, active: true });
    };

    const handleTouchEnd = () => {
      isInteracting.current = false;
      onPointerUpdate?.({ x: -1000, y: -1000, isTouch: true, active: false });
    };

    // Listeners optimizados
    canvas.addEventListener("pointermove", handlePointerMove, { passive: true });
    canvas.addEventListener("pointerleave", handlePointerLeave, { passive: true });
    canvas.addEventListener("touchstart", handleTouchStart, { passive: true });
    canvas.addEventListener("touchmove", handleTouchMove, { passive: true });
    canvas.addEventListener("touchend", handleTouchEnd, { passive: true });
    canvas.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      canvas.removeEventListener("pointermove", handlePointerMove);
      canvas.removeEventListener("pointerleave", handlePointerLeave);
      canvas.removeEventListener("touchstart", handleTouchStart);
      canvas.removeEventListener("touchmove", handleTouchMove);
      canvas.removeEventListener("touchend", handleTouchEnd);
      canvas.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [canvasRef, onPointerUpdate]);
}
